import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { Avatar } from "@mui/material";
import CustomerRates from "../TabsCard/CustomerRates";
import EngineerRates from "../TabsCard/EngineerRates";
import EditClientApprovalTimeLogModal from "../components/EditClientApprovalTimeLogModal";
import EditEngineerApprovalTimeLogModal from "../components/EditEngineerApprovalTimeLogModal";
import { useState } from "react";
import {
  approveWorkOrder,
  fetchWorkOrderPaymetDetails,
  reviewWorkOrder,
  verifyWorkOrder,
} from "../../../services/api/workOrder";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { getTimeZoneAbbreviation } from "../../../utils/getTimeZoneAbbreviation";
import { displayFormattedTotalTimelog } from "../../../utils/helpers";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import TimeLogs from "../TabsCard/TimeLogs";
import EditTimeLogModal from "../components/EditTimeLogModal";
import { set } from "lodash";
import UserProfileLink from "../../../components/ui/UserProfileLink";

const ApproveWorkOrderModal = ({
  isApproveModalOpen,
  setIsApproveModalOpen,
  workOrderDetail,
  isVerified,
  setIsVerified,
  isReviewed,
  setIsReviewed,
}) => {
  const dispatch = useDispatch();
  const { id: workOrderId } = useParams();
  const { userData: authUser } = useCurrentUser();
  const { updateWorkOrder } = useUpdateWorkOrder();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const {
    isLoading,
    error,
    refetch: refetchworkOrderPaymentData,
    data: workOrderPaymentData,
  } = useQuery({
    queryKey: ["workOrderPayment"],
    queryFn: () => fetchWorkOrderPaymetDetails(workOrderId),
    enabled: !!workOrderId,
  });

  const payment = workOrderPaymentData?.data;

  const {
    isPending: isWorkOrderApprovedLoading,
    error: isWorkOrderApprovedError,
    mutateAsync: approveWorkOrderMutation,
  } = useMutation({
    mutationFn: approveWorkOrder,
  });
  const {
    isPending: isReviewingWorkOrderApiLoading,
    error: isReviewingWorkOrderApiError,
    mutateAsync: reviewWorkOrdersMutation,
  } = useMutation({
    mutationFn: reviewWorkOrder,
  });

  const {
    isPending: isVerifyingWorkOrderApiLoading,
    error: isVerifyingWorkOrderApiError,
    mutateAsync: verifyWorkOrdersMutation,
  } = useMutation({
    mutationFn: verifyWorkOrder,
  });

  const handleEditClick = (log, type) => {
    setSelectedLog({ ...log, type });
    setIsEditOpen(true);
  };
  const handleReviewWorkOrder = async () => {
    try {
      const reviewWorkOrderResponse = await reviewWorkOrdersMutation(workOrderId);
      await updateWorkOrder();
      setIsReviewed(true);
      dispatch(
        showToast({
          message: "Work Order Reviewed Successfully",
          type: "success",
        }),
      );
      setIsApproveModalOpen(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
        }),
      );
    }
  };
  const handleVerifyWorkOrder = async () => {
    try {
      const verifyWorkOrderResponse = await verifyWorkOrdersMutation(workOrderId);
      await updateWorkOrder();
      setIsVerified(true);
      dispatch(
        showToast({
          message: "Work Order Verified Successfully",
          type: "success",
        }),
      );
      setIsApproveModalOpen(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
        }),
      );
    }
  };

  const approveWorkOrderApi = async () => {
    try {
      await approveWorkOrderMutation(workOrderId);
      await updateWorkOrder();
      setIsApproveModalOpen(false);
      dispatch(
        showToast({
          message: "Successfully Approved",
          type: "success",
        }),
      );
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  const timeIn = workOrderDetail?.timeLogs[0] || null;
  const timeOut = workOrderDetail?.timeLogs[workOrderDetail?.timeLogs?.length - 1] || null;

  return (
    <BaseModal
      className="overflow-auto"
      open={isApproveModalOpen}
      close={() => setIsApproveModalOpen(false)}
    >
      <div className="py-3 sm:py-7 px-4 sm:px-12 w-full sm:w-[800px] lg:w-[1000px] xl:w-[1400px]">
        <div className="flex items-start border-bottom mb-4 pb-1">
          <h5 className="text-dark">
            {`Time Sheet ${!isReviewed && !isVerified ? "Review" : isReviewed && isVerified ? "Approval" : "Verification"}`}
            {isReviewed && !isVerified && (
              <span className="text-[13px] ml-2 text-red-600 block sm:inline">
                (Please verify the timesheet and confirm whether a PO is required for this project)
              </span>
            )}
          </h5>
        </div>

        <div className="border rounded-md px-4 sm:px-8 py-4 sm:py-6 mb-8">
          <div className="flex flex-wrap gap-y-2 items-center justify-between border-b pb-2">
            <div className="flex items-center">
              <p className="mr-2">Logged By </p>
              <UserProfileLink
                as="div"
                userId={workOrderDetail?.engineer?.id}
                search="?direct=true"
                className="flex items-center"
                linkClassName="cursor-pointer hover:underline"
              >
                <Avatar
                  className={`h-[30px] w-[30px] mr-2`}
                  src={workOrderDetail?.engineer.profilePicture}
                />
                <p className="text-primary font-medium text-base">
                  {`${workOrderDetail?.engineer?.firstName} ${workOrderDetail?.engineer?.lastName}`}
                </p>
              </UserProfileLink>
            </div>
            <div className="flex items-center">
              <p className="mr-2">
                {!isReviewed && !isVerified
                  ? "Reviewed By"
                  : isReviewed && isVerified
                    ? "Approved By"
                    : "Verified By"}
              </p>
              <UserProfileLink
                as="div"
                userId={authUser?.id}
                className="flex items-center"
                linkClassName="cursor-pointer hover:underline"
              >
                <Avatar className="h-[30px] w-[30px] mr-2" src={authUser?.picture.url} />
                <p className="text-secondary font-medium text-base">
                  {`${authUser?.firstName} ${authUser?.lastName}`}
                </p>
              </UserProfileLink>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 items-center sm:mx-8 mt-4">
            <h6 className="!text-primary font-medium">
              <span className="font-normal">Total Time</span> <br />
              {displayFormattedTotalTimelog(payment?.engineerRates?.hoursWorked)}
            </h6>

            <div className="py-1.5 px-8 ">
              <p className="font-normal w-36 text-base">Time In</p>
              <div className="flex items-center mt-2">
                <i class="fa-regular fa-clock bg-[#686868] rounded px-[6px] py-[2px] text-2xl text-white"></i>
                <div className="ml-2">
                  <p className="leading-none mb-1">
                    {timeIn?.checkInTime
                      ? dayjs(timeIn.checkInTime)
                          .tz(workOrderDetail?.address?.timeZone)
                          .format("MMM D, YYYY hh:mm A")
                      : "N/A"}
                  </p>
                  <p className="leading-none">
                    {workOrderDetail?.address?.timeZone} (
                    {getTimeZoneAbbreviation(workOrderDetail?.address?.timeZone)})
                  </p>
                </div>
              </div>
            </div>
            <div className="py-1.5 px-8 relative">
              {/* Pencil Icon - Top Right */}
              <i
                className="fa-light fa-pencil absolute top-2 right-2 text-[#686868] cursor-pointer"
                onClick={() => handleEditClick(timeIn, "IN")}
              />

              <p className="font-normal w-36 text-base">Time Out</p>

              <div className="flex items-center mt-2">
                <i className="fa-regular fa-clock bg-[#686868] rounded px-[6px] py-[2px] text-2xl text-white"></i>

                <div className="ml-2">
                  <p className="leading-none mb-1">
                    {timeOut?.checkOutTime
                      ? dayjs(timeOut.checkOutTime)
                          .tz(workOrderDetail?.address?.timeZone)
                          .format("MMM D, YYYY hh:mm A")
                      : "N/A"}
                  </p>

                  <p className="leading-none">
                    {workOrderDetail?.address?.timeZone} (
                    {getTimeZoneAbbreviation(workOrderDetail?.address?.timeZone)})
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <CustomerTimeSheet
          payment={payment?.clientRates}
          authUser={authUser}
          workOrderStatus={workOrderDetail.status}
          refetchworkOrderPaymentData={refetchworkOrderPaymentData}
          isVerified={isVerified}
          isReviewed={isReviewed}
        />
        <EngineerTimeSheet
          authUser={authUser}
          workOrderStatus={workOrderDetail.status}
          payment={payment?.engineerRates}
          refetchworkOrderPaymentData={refetchworkOrderPaymentData}
          isVerified={isVerified}
          isReviewed={isReviewed}
        />

        <div className="flex items-center justify-end bg-white py-6 mt-3">
          <BaseButton
            variant="outlined"
            // disabled={isEngineerFeedbackApiLoading}
            // isLoading={isEngineerFeedbackApiLoading}

            className="py-[4px] px-[28px] ml-4 rounded-none"
            color="primary"
            fullWidth={false}
            onClick={() => setIsApproveModalOpen(false)}
          >
            Cancel
          </BaseButton>

          <BaseButton
            isLoading={isLoading}
            className="py-[5px] px-[28px] ml-4 rounded-none"
            color="primary"
            fullWidth={false}
            onClick={
              !isReviewed && !isVerified
                ? handleReviewWorkOrder
                : isReviewed && isVerified
                  ? approveWorkOrderApi
                  : handleVerifyWorkOrder
            }
          >
            {!isReviewed && !isVerified
              ? "Review"
              : isReviewed && isVerified
                ? "Approve"
                : "Verify"}
          </BaseButton>
        </div>
      </div>
      {isEditOpen && (
        <EditTimeLogModal
          log={selectedLog}
          onClose={() => setIsEditOpen(false)}
          refetchworkOrderPaymentData={refetchworkOrderPaymentData}
        />
      )}
    </BaseModal>
  );
};

export default ApproveWorkOrderModal;

function CustomerTimeSheet({
  payment,
  authUser,
  workOrderStatus,
  refetchworkOrderPaymentData,
  isVerified,
  isReviewed,
}) {
  const [isEditClientApprovalTimeLogModalOpen, setIsEditClientApprovalTimeLogModalOpen] =
    useState(false);
  return (
    <div className="border rounded-md px-4 sm:px-8 py-4 sm:py-6">
      <div className="flex flex-wrap gap-y-2 items-center justify-between w-full border-b pb-1">
        <h5 className="text-secondary">Customer Time Sheet</h5>
        <div className="flex items-center">
          <p className="text-secondary mr-4 sm:mr-8">
            {!isReviewed && !isVerified
              ? "Reviewed Time"
              : isReviewed && isVerified
                ? "Approved Time"
                : "Verified Time"}
            {"  "}
            <span className="font-medium text-lg ml-1">
              {displayFormattedTotalTimelog(payment?.totalHoursApproved || payment?.hoursWorked)}
            </span>
          </p>

          {(payment?.paymentType === "HOURLY" ||
            payment?.paymentType === "HALF_DAY" ||
            payment?.paymentType === "FULL_DAY" ||
            payment?.paymentType === "CUSTOM_HOURS" ||
            payment?.paymentType === "FTH") && (
            <i
              class="fa-light fa-pencil cursor-pointer text-secondary"
              onClick={() => setIsEditClientApprovalTimeLogModalOpen(true)}
            ></i>
          )}
        </div>
      </div>

      <div className="border rounded-md mt-6">
        <CustomerRates
          mode="approval"
          payment={payment}
          paymentType={payment?.paymentType}
          status={workOrderStatus}
          authRole={authUser.role.role}
        />
      </div>

      {isEditClientApprovalTimeLogModalOpen && (
        <EditClientApprovalTimeLogModal
          isEditClientApprovalTimeLogModalOpen={isEditClientApprovalTimeLogModalOpen}
          setIsEditClientApprovalTimeLogModalOpen={setIsEditClientApprovalTimeLogModalOpen}
          payment={payment}
          refetchworkOrderPaymentData={refetchworkOrderPaymentData}
        />
      )}
    </div>
  );
}

function EngineerTimeSheet({
  payment,
  workOrderStatus,
  refetchworkOrderPaymentData,
  authUser,
  isVerified,
  isReviewed,
}) {
  const [isEditEngineerApprovalTimeLogModalOpen, setIsEditEngineerApprovalTimeLogModalOpen] =
    useState(false);
  return (
    <div className="border rounded-md px-4 sm:px-8 py-4 sm:py-6 mt-9">
      <div className="flex flex-wrap gap-y-2 items-center justify-between w-full border-b pb-1">
        <h5 className="text-primary">Engineer Time Sheet</h5>

        <div className="flex items-center">
          <p className="text-primary mr-4 sm:mr-8">
            {!isReviewed && !isVerified
              ? "Reviewed Time"
              : isReviewed && isVerified
                ? "Approved Time"
                : "Verified Time"}
            {"  "}
            <span className="font-medium text-lg ml-1">
              {displayFormattedTotalTimelog(payment?.totalHoursApproved || payment?.hoursWorked)}
            </span>
          </p>

          {(payment?.paymentType === "HOURLY" ||
            payment?.paymentType === "HALF_DAY" ||
            payment?.paymentType === "FULL_DAY") &&
            payment?.minimumHours > 0 && (
              <i
                class="fa-light fa-pencil cursor-pointer text-primary"
                onClick={() => setIsEditEngineerApprovalTimeLogModalOpen(true)}
              ></i>
            )}
        </div>
      </div>

      <div className="border rounded-md mt-6">
        <EngineerRates
          mode="approval"
          payment={payment}
          status={workOrderStatus}
          paymentType={payment?.paymentType}
        />
      </div>

      {isEditEngineerApprovalTimeLogModalOpen && (
        <EditEngineerApprovalTimeLogModal
          isEditEngineerApprovalTimeLogModalOpen={isEditEngineerApprovalTimeLogModalOpen}
          setIsEditEngineerApprovalTimeLogModalOpen={setIsEditEngineerApprovalTimeLogModalOpen}
          payment={payment}
          refetchworkOrderPaymentData={refetchworkOrderPaymentData}
        />
      )}
    </div>
  );
}
